import { PopoverPlacement } from "@idui/react-popover";
import { get, isNil, map } from "lodash";
import { FC, useEffect, useMemo, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useDropdownSourceContext } from "@ryde/redux";
import {
  capitalize,
  DATA_E2E_KEY,
  IStyledAttributes,
  PLURAL_KEY,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../common";
import { useDecorateWithMark } from "../../utils";
import { IRenderColumnTemplate } from "./_core";
import type { ICsvCellViewData } from "./csv-list.models";
import { buildCsvCellViewData } from "./csv-list.utils";

export const RestCsvListWrapperStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "rest-csv-list-wrapper",
})`
  color: ${rydeColors.lightBlue};
  font-weight: 400;
  margin-inline-start: 4px;
`;

export const CsvCellTemplate: FC<
  IRenderColumnTemplate & { customClassName?: string }
> = ({ value: rawValue, formAttributes, gridAttributes, customClassName }) => {
  const { decorateValue } = useDecorateWithMark();
  const { getDropdownLabels } = useDropdownSourceContext();
  const [displayValues, setDisplayValues] = useState<string[]>([]);

  const { dropItemsSource } = formAttributes || {};
  const {
    numberOfItemsToShow,
    showHiddenItemsAsEllipsis,
    csvListPath,
    csvListMapFieldName,
  } = gridAttributes || {};
  const { t } = useTranslation();
  const [, startTransition] = useTransition();

  const value = useMemo(() => {
    if (!csvListPath) {
      return rawValue;
    }

    const valueFromPath = get(rawValue, csvListPath);

    if (!isNil(valueFromPath)) {
      return [].concat(valueFromPath);
    }

    return valueFromPath;
  }, [csvListPath, rawValue]);

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      if (dropItemsSource) {
        const mappedValue = csvListMapFieldName
          ? map(value, csvListMapFieldName)
          : value;
        const labels = await getDropdownLabels(
          dropItemsSource,
          mappedValue ?? [],
        );
        setDisplayValues(labels);
      } else {
        if (Array.isArray(value) && value?.length) {
          const mappedValue = csvListMapFieldName
            ? map(value, csvListMapFieldName)
            : value;
          setDisplayValues(mappedValue?.map(String));
        }
      }
    };

    startTransition(() => {
      getItems();
    });
  }, [value, dropItemsSource, getDropdownLabels, csvListMapFieldName]);

  const csvCellViewData: ICsvCellViewData = useMemo(
    () =>
      buildCsvCellViewData({
        displayValues,
        numberOfItemsToShow,
        showHiddenItemsAsEllipsis,
        tooltipPrefix: capitalize(
          dropItemsSource ? `${t(`${dropItemsSource}.${PLURAL_KEY}`)}: ` : "",
        ),
      }),
    [
      displayValues,
      dropItemsSource,
      numberOfItemsToShow,
      showHiddenItemsAsEllipsis,
      t,
    ],
  );

  const highlightedDisplayValue = useMemo(() => {
    return decorateValue(csvCellViewData.displayValue);
  }, [csvCellViewData.displayValue, decorateValue]);

  return (
    <div
      className={customClassName ?? "cell_simple"}
      data-e2e-type="csv-list-template"
    >
      <FramerTooltip
        content={csvCellViewData.tooltipContent}
        placement={PopoverPlacement.bottom}
      >
        <>
          <span>{highlightedDisplayValue}</span>{" "}
          {Boolean(csvCellViewData.moreItemsCount) && (
            <RestCsvListWrapperStyled>
              {`+${csvCellViewData.moreItemsCount}`}
            </RestCsvListWrapperStyled>
          )}
        </>
      </FramerTooltip>
    </div>
  );
};
